import { nextFallbackLevel } from "@/components/aposEngine/simpleEngine";

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/[^a-z0-9\s'-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const containsAny = (text, phrases) =>
  phrases.some((phrase) => text.includes(phrase));

const declinePhrases = (subject) => [
  `i don't want ${subject}`,
  `i dont want ${subject}`,
  `i do not want ${subject}`,
  `don't want ${subject}`,
  `dont want ${subject}`,
  `do not want ${subject}`,
];

export const COMPANION_QUICK_ACTIONS = [
  {
    key: "explain",
    label: "Why does this help?",
    prompt: "Why does this help my current state? Keep the answer brief.",
  },
  {
    key: "workout",
    label: "Build today’s workout",
    prompt: "Build me a workout from today’s matched output and let me add it to my APOS calendar.",
  },
  {
    key: "schedule",
    label: "What is scheduled?",
    prompt: "What do I have scheduled next, and how should I approach it?",
  },
  {
    key: "moderate",
    label: "Give me a moderate option",
    prompt: "I do not want high output. Give me a familiar moderate option.",
  },
  {
    key: "no-exercise",
    label: "I do not want exercise",
    prompt: "I do not want to exercise. Use a sport or recreation option instead.",
  },
  {
    key: "different-activity",
    label: "I do not want that activity",
    prompt: "I do not want that sport or activity. Give me a similar sport, game, or skill option.",
  },
  {
    key: "somatic",
    label: "Use a somatic option",
    prompt: "I do not want exercise or recreation. Give me a somatic, breathing, or mobility option.",
  },
  {
    key: "minimum",
    label: "Give me the smallest step",
    prompt: "Give me the smallest useful action I can do right now.",
  },
];

export function classifyCompanionIntent(
  message,
  {
    currentFallback = "primary",
    hasSelectedActivity = false,
    selectedActivityLabel = "",
  } = {},
) {
  const text = normalize(message);
  const selectedLabel = normalize(selectedActivityLabel);

  if (!text) return null;

  if (
    containsAny(text, [
      "add this workout to my calendar",
      "add this workout to calendar",
      "add the workout to my calendar",
      "add the workout to calendar",
      "add it to my calendar",
      "add it to calendar",
      "put this workout on my calendar",
      "put it on my calendar",
      "save this workout to my calendar",
      "schedule this workout",
      "schedule it in my calendar",
    ])
  ) {
    return { type: "calendar-add-workout", reason: "calendar-add-request" };
  }

  if (
    containsAny(text, [
      "build me a workout",
      "build a workout for me",
      "build today's workout",
      "build todays workout",
      "create me a workout",
      "create a workout for me",
      "make me a workout",
      "give me a workout",
      "plan me a workout",
      "plan a workout for me",
      "write me a workout",
      "what workout should i do",
      "what workout can i do",
      "workout for today",
      "today's workout",
      "todays workout",
      "exercise routine for today",
      "training session for today",
    ])
  ) {
    return { type: "workout-request", reason: "workout-request" };
  }

  if (
    containsAny(text, [
      "smallest step",
      "smallest useful step",
      "smallest useful action",
      "minimum step",
      "minimum useful input",
      "do nothing",
      "nothing active",
      ...declinePhrases("breathing"),
      ...declinePhrases("somatic"),
      ...declinePhrases("stretching"),
      "no somatic",
    ])
  ) {
    return { type: "rematch", fallbackLevel: "minimum", reason: "minimum-request" };
  }

  if (
    containsAny(text, [
      "somatic",
      "breathing option",
      "mobility option",
      "quiet option",
      "no exercise or recreation",
      ...declinePhrases("sport"),
      ...declinePhrases("recreation"),
      "no sport",
      "no recreation",
    ])
  ) {
    return { type: "rematch", fallbackLevel: "somatic", reason: "somatic-request" };
  }

  if (
    containsAny(text, [
      "similar sport",
      "similar game",
      "similar activity",
      "different sport",
      "different game",
      "different activity",
      ...declinePhrases("that activity"),
      ...declinePhrases("that sport"),
    ]) ||
    (selectedLabel &&
      text.includes(selectedLabel) &&
      containsAny(text, [
        "don't want",
        "dont want",
        "do not want",
        "not doing",
        "won't do",
        "wont do",
      ]))
  ) {
    return { type: "rematch", fallbackLevel: "similar-activity", reason: "selected-declined" };
  }

  if (
    containsAny(text, [
      "use my sport",
      "use my activity",
      "use recreation",
      ...declinePhrases("to exercise"),
      ...declinePhrases("exercise"),
      "no exercise",
      ...declinePhrases("a workout"),
      ...declinePhrases("to work out"),
      "not exercising",
    ])
  ) {
    return {
      type: "rematch",
      fallbackLevel: hasSelectedActivity ? "selected-activity" : "similar-activity",
      reason: "exercise-declined",
    };
  }

  if (
    containsAny(text, [
      "moderate option",
      "lower intensity",
      "less intense",
      "too intense",
      "too hard",
      "not high output",
      ...declinePhrases("high output"),
      "no high intensity",
    ])
  ) {
    return { type: "rematch", fallbackLevel: "moderate", reason: "high-output-declined" };
  }

  if (
    containsAny(text, [
      "shorter option",
      "less time",
      "only two minutes",
      "only 2 minutes",
      "only three minutes",
      "only 3 minutes",
      "only have two minutes",
      "only have 2 minutes",
      "only have three minutes",
      "only have 3 minutes",
      "i only have two minutes",
      "i only have 2 minutes",
      "i only have three minutes",
      "i only have 3 minutes",
      "i have no time",
      "very little time",
    ])
  ) {
    return {
      type: "rematch",
      fallbackLevel: currentFallback,
      minutes: 3,
      reason: "shorter-request",
    };
  }

  if (
    containsAny(text, [
      "another option",
      "something else",
      ...declinePhrases("this"),
      "not this",
      "no thanks",
    ])
  ) {
    return {
      type: "rematch",
      fallbackLevel: nextFallbackLevel(currentFallback),
      reason: "next-option",
    };
  }

  return null;
}

export function formatProblemSolvingSummary(plan) {
  if (!plan?.immediate) {
    return "I could not form a new option from the current check-in.";
  }
  const preparation = plan.preparation?.steps?.[0]
    ? ` Before you begin: ${plan.preparation.steps[0]}`
    : "";
  const cooldown = plan.cooldown?.instruction
    ? ` Finish with: ${plan.cooldown.instruction}`
    : "";
  return `${plan.immediate.title}. ${plan.immediate.instruction}${preparation}${cooldown}`;
}
